import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parse, format, isValid } from 'date-fns';

/**
 * DateInput Component - Date picker that displays in DD/MM/YYYY format (India format)
 *
 * Uses react-datepicker for proper date format control
 *
 * Props:
 * - value: Date string in YYYY-MM-DD format (ISO)
 * - onChange: Callback with event-like object { target: { value: 'YYYY-MM-DD' } }
 * - onBlur: Blur callback
 * - className: CSS class for styling
 * - placeholder: Placeholder text
 * - disabled: Disable the input
 * - max: Maximum date in YYYY-MM-DD format
 * - min: Minimum date in YYYY-MM-DD format
 */
const DateInput = ({
  value,
  onChange,
  onBlur,
  className = '',
  placeholder = 'DD/MM/YYYY',
  disabled = false,
  max,
  min,
  style = {}
}) => {
  // Convert YYYY-MM-DD string to Date object
  const parseISODate = (isoString) => {
    if (!isoString) return null;
    // Ensure it's a string before parsing
    const strValue = typeof isoString === 'string' ? isoString : String(isoString);
    // Handle ISO datetime format (trim time portion if present)
    const dateOnly = strValue.split('T')[0];
    const parsed = parse(dateOnly, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : null;
  };

  // Convert Date object to YYYY-MM-DD string
  const toISOString = (date) => {
    if (!date || !isValid(date)) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const selectedDate = parseISODate(value);
  const minDate = parseISODate(min);
  const maxDate = parseISODate(max);

  const handleChange = (date) => {
    // Create an event-like object to maintain compatibility with existing code
    const syntheticEvent = {
      target: {
        value: toISOString(date)
      }
    };
    onChange(syntheticEvent);
  };

  const handleBlur = () => {
    if (onBlur) {
      const syntheticEvent = {
        target: {
          value: value || ''
        }
      };
      onBlur(syntheticEvent);
    }
  };

  return (
    <DatePicker
      selected={selectedDate}
      onChange={handleChange}
      onBlur={handleBlur}
      dateFormat="dd/MM/yyyy"
      placeholderText={placeholder}
      disabled={disabled}
      minDate={minDate}
      maxDate={maxDate}
      className={className}
      wrapperClassName="date-input-wrapper"
      showYearDropdown
      showMonthDropdown
      dropdownMode="select"
      yearDropdownItemNumber={100}
      scrollableYearDropdown
    />
  );
};

export default DateInput;
